import get from 'lodash/get';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import ApiRequests from '../http/ApiRequests';
import axiosErrorHandler from '../http/AxiosErrorHandler';
import auditFilterSchema from '../schemas/auditFilterSchema';
import RoboadvisorService from '../services/roboadvisor.service';
import validationService from '../services/validationService';
import moment from 'moment';

const useAuditTrails = () => {
    const api = useMemo(() => new ApiRequests(), []),
        { register, handleSubmit, errors, control, getValues } = useForm(),
        validator = validationService(),
        initialGridMeta = useMemo(
            () => ({
                limit: 100,
                offset: 0,
                total: 0,
            }),
            []
        ),
        [generalErrorList, setGeneralErrorList] = useState([]),
        // [successMessage, setSuccessMessage] = useState(null),
        [gridData, setGridData] = useState([]),
        [gridMeta, setGridMeta] = useState({ ...initialGridMeta }),
        [gridFilter, setGridFilter] = useState({ after: moment().subtract(7, 'days')._d }),
        [filerEntityTypes, setFilerEntityTypes] = useState([]),
        gridHeaders = [
            { name: 'User', key: 'user', filter: false },
            { name: 'Description', key: 'description', filter: false },
            { name: 'Time', key: 'timestamp', formatter: 'datetime', filter: false },
            { name: 'Action', key: 'action', filter: false, formatter: 'const' },
            { name: 'Type', key: 'type', filter: false, formatter: 'const' },
        ],
        onSubmit = data => {
            const obj = {};
            if (data.after && data.before && data.after > data.before) {
                setGeneralErrorList([validator.errorMessages.DATE_FILTER_ERROR]);
                return;
            }
            if (data.after) obj['after'] = data.after;
            if (data.before) obj['before'] = data.before;
            if (data.entityType) obj['entityType'] = data.entityType;

            setGridFilter(obj);
            getAuditData(obj, {});
        },
        // the filter includes tha meta as well
        getAuditData = useCallback(
            (filter, meta) => {
                api.getAuditData({ ...filter, ...meta })
                    .then(res => {
                        setGridData(processData(get(res, 'data.entries', [])));

                        setGridMeta({
                            limit: get(res, 'data.limit', 100),
                            offset: get(res, 'data.offset', 0),
                            total: get(res, 'data.total', 0),
                        });
                    })
                    .catch(err => {
                        const [errorList] = axiosErrorHandler(err);
                        setGeneralErrorList(errorList);
                    });
            },
            [api]
        ),
        onGridPage = event => {
            getAuditData(gridFilter, { limit: event.rows, offset: event.first });
        },
        // if name or content of an audit item used for description
        processData = data => {
            return data.map(item => {
                const name = get(item, 'state.name', ''),
                    content = get(item, 'state.content', ''),
                    messageContent = get(item, 'state.messageContent', ''),
                    filename = get(item, 'state.filename', ''),
                    description = get(item, 'state.description', ''),
                    pageId = get(item, 'state.pageId', ''),
                    permission = get(item, 'state.permission', ''),
                    confidenceThreshold = get(item, 'state.confidenceThreshold', ''),
                    forwardingThreshold = get(item, 'state.forwardingThreshold', ''),
                    responsesEnabled = get(item, 'state.responsesEnabled', '');

                if (name) {
                    item['description'] = name;
                } else if (messageContent) {
                    item['description'] =
                        'Content: ' + messageContent + 'Description: ' + description;
                } else if (pageId) {
                    item['description'] = pageId;
                } else if (content) {
                    item['description'] = content;
                } else if (permission) {
                    item['description'] = permission.toLocaleLowerCase().replaceAll('_', ' ');
                } else if (filename) {
                    item['description'] = filename;
                } else if (
                    confidenceThreshold &&
                    forwardingThreshold &&
                    typeof responsesEnabled === 'boolean'
                ) {
                    item['description'] = `Responses: ${
                        responsesEnabled ? 'ON' : 'OFF'
                    }, Confidence Threshold: ${confidenceThreshold}, Forwarding Threshold: ${forwardingThreshold}`;
                } else {
                    item['description'] = '';
                }
                return item;
            });
        },
        setEntityTypesOptions = () => {
            return [
                { label: '', value: '' },
                ...RoboadvisorService.entityTypes.map(item => {
                    return { value: item, label: item.toLocaleLowerCase().replaceAll('_', ' ') };
                }),
            ];
        };

    useEffect(() => {
        getAuditData({ after: getValues('after') }, initialGridMeta);
        setFilerEntityTypes(setEntityTypesOptions());
    }, [getAuditData, getValues, initialGridMeta]);

    return {
        registerValidationFor: auditFilterSchema(register),
        gridData,
        gridMeta,
        generalErrorList,
        filerEntityTypes,
        // successMessage,
        onGridPage,
        gridHeaders,
        fieldErrorFor: errors,
        control,
        onSubmit: handleSubmit(
            onSubmit,
            validator.extractErrorsFromInvalidForm(setGeneralErrorList)
        ),
    };
};

export default useAuditTrails;
